import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import QRCode from "qrcode";
import api, { formatApiError } from "@/lib/api";
import { money, asset } from "@/lib/format";
import {
  Search, Leaf, Beef, Plus, Minus, Trash2, ChefHat, QrCode, CheckCircle2,
  ArrowLeft, Users, Loader2, UtensilsCrossed,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const KS_LABEL = {
  awaiting_approval: "Waiting for manager",
  queued: "Queued in kitchen",
  cooking: "Cooking",
  ready: "Food ready",
  handed: "With waiter",
};

export default function WaiterPortal() {
  const qc = useQueryClient();
  const [table, setTable] = useState(null);
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [qrUrl, setQrUrl] = useState("");
  const [upiInfo, setUpiInfo] = useState(null);
  const [sending, setSending] = useState(false);

  const { data: tables = [] } = useQuery({
    queryKey: ["tables"],
    queryFn: async () => (await api.get("/tables")).data,
    refetchInterval: 5000,
  });
  const { data: cats = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/categories")).data });
  const { data: menu = [] } = useQuery({ queryKey: ["menu"], queryFn: async () => (await api.get("/menu")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const { data: running = [] } = useQuery({
    queryKey: ["waiter-bill", table?.id],
    queryFn: async () => (await api.get(`/orders?table_id=${table.id}&unpaid=true`)).data,
    enabled: !!table,
    refetchInterval: 4000,
  });
  const { data: calls = [] } = useQuery({
    queryKey: ["calls"],
    queryFn: async () => (await api.get("/calls")).data,
    refetchInterval: 3000,
  });
  const bill = running[0];
  const sym = settings?.currency_symbol || "₹";
  const flow = settings?.kitchen_order_flow === "approve" ? "approve" : "direct";

  const filtered = useMemo(() => menu.filter((m) => m.available
    && (activeCat === "all" || m.category_id === activeCat)
    && (!search || m.name.toLowerCase().includes(search.toLowerCase()))), [menu, activeCat, search]);

  const add = (item) => {
    setCart((c) => {
      const ex = c.find((x) => x.menu_item_id === item.id);
      if (ex) return c.map((x) => x.menu_item_id === item.id ? { ...x, qty: x.qty + 1 } : x);
      return [...c, { menu_item_id: item.id, name: item.name, price: item.price, qty: 1, tax_rate: item.tax_rate }];
    });
  };

  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const tax = cart.reduce((s, i) => s + i.price * i.qty * ((i.tax_rate || 5) / 100), 0);
  const cartTotal = Math.max(subtotal + tax, 0);

  const sendKitchen = async () => {
    if (!table) return toast.error("Choose a table");
    if (!cart.length) return toast.error("Add items first");
    setSending(true);
    try {
      if (bill) {
        await api.post(`/orders/${bill.id}/add-items`, { items: cart });
        toast.success(flow === "approve" && bill.status === "awaiting_approval"
          ? "Items added — still waiting for manager"
          : "Items sent to kitchen");
      } else {
        const { data: order } = await api.post("/orders", {
          order_type: "dine-in", table_id: table.id, channel: "waiter", items: cart,
        });
        toast.success(order.status === "awaiting_approval"
          ? `${order.order_number} sent for manager approval`
          : `${order.order_number} sent to kitchen`);
      }
      setCart([]);
      qc.invalidateQueries({ queryKey: ["tables"] });
      qc.invalidateQueries({ queryKey: ["waiter-bill"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send order");
    } finally {
      setSending(false);
    }
  };

  const openUpi = async () => {
    if (!bill) return toast.error("No running bill");
    try {
      const { data } = await api.get(`/payments/upi/${bill.id}`);
      setUpiInfo(data);
      setQrUrl(await QRCode.toDataURL(data.uri || data.qr_image_url || "", { width: 320, margin: 1 }));
      setPayOpen(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "UPI QR not available");
    }
  };

  const markPaid = async () => {
    if (!bill) return;
    await api.post(`/orders/${bill.id}/pay`, { splits: [{ method: "upi", amount: bill.total }] });
    toast.success(`Table ${table.name} paid · ${money(bill.total, sym)}`);
    setPayOpen(false);
    setTable(null);
    qc.invalidateQueries({ queryKey: ["tables"] });
    qc.invalidateQueries({ queryKey: ["waiter-bill"] });
  };

  useEffect(() => { setCart([]); setPayOpen(false); setUpiInfo(null); }, [table?.id]);

  // Auto-confirm UPI when Razorpay QR / webhook marks the order paid
  useEffect(() => {
    if (!payOpen || !bill?.id || !upiInfo?.auto_confirm) return undefined;
    if (upiInfo.payment_status === "paid") return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/payments/upi/${bill.id}/status`);
        if (data.payment_status === "paid") {
          toast.success(`UPI auto-confirmed · ${money(bill.total, sym)}`);
          setPayOpen(false);
          setTable(null);
          qc.invalidateQueries({ queryKey: ["tables"] });
          qc.invalidateQueries({ queryKey: ["waiter-bill"] });
        }
      } catch { /* keep polling */ }
    }, 2500);
    return () => clearInterval(t);
  }, [payOpen, bill?.id, bill?.total, upiInfo?.auto_confirm, upiInfo?.payment_status, qc, sym]);

  if (!table) {
    return (
      <div className="h-full overflow-y-auto p-5 lg:p-8" data-testid="waiter-portal">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-[var(--kul-primary)] flex items-center justify-center">
            <UtensilsCrossed className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-800 tracking-tight">Waiter Portal</h1>
            <p className="text-sm text-neutral-500">Pick a table to take an order</p>
          </div>
        </div>
        {!!calls.length && (
          <div className="mb-4 kul-card p-3 flex flex-wrap gap-2">
            {calls.map((c) => (
              <button key={c.id} onClick={async () => { await api.post(`/calls/${c.id}/ack`); toast.success(`Table ${c.table_name} ok`); qc.invalidateQueries({ queryKey: ["calls"] }); }}
                className="px-3 py-1.5 rounded-full bg-[var(--kul-primary)] text-white text-xs font-semibold">
                Table {c.table_name} · {c.type}
              </button>
            ))}
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3">
          {tables.map((t) => (
            <button key={t.id} onClick={() => setTable(t)} data-testid={`waiter-table-${t.id}`}
              className={`kul-card p-4 text-left active:scale-[0.98] transition-transform ${
                t.status === "occupied" ? "ring-1 ring-[var(--kul-primary)]" : ""}`}>
              <div className="font-display text-xl font-800">{t.name}</div>
              <div className="text-xs text-neutral-500 mt-1 flex items-center gap-1"><Users className="w-3 h-3" /> {t.seats} seats</div>
              <div className={`mt-2 text-[11px] font-semibold uppercase tracking-wide ${
                t.status === "available" ? "text-green-600" : t.status === "occupied" ? "text-[var(--kul-primary)]" : "text-purple-600"}`}>
                {t.status}
              </div>
            </button>
          ))}
        </div>
        {!tables.length && <div className="text-center text-neutral-400 py-24">No tables yet. Ask a manager to add tables.</div>}
      </div>
    );
  }

  const ks = bill?.kitchen_status || (bill?.status === "preparing" ? "cooking" : bill?.status === "ready" ? "ready" : bill?.status === "served" ? "handed" : bill?.status === "awaiting_approval" ? "awaiting_approval" : "queued");

  return (
    <div className="flex h-full" data-testid="waiter-order">
      <div className="flex-1 flex flex-col overflow-hidden border-r border-[var(--kul-border)]">
        <div className="p-4 bg-white border-b border-[var(--kul-border)]">
          <button onClick={() => setTable(null)} className="text-sm text-neutral-500 flex items-center gap-1 mb-3 hover:text-[var(--kul-primary)]">
            <ArrowLeft className="w-4 h-4" /> All tables
          </button>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-display text-xl font-800">Table {table.name}</h2>
              {bill && <p className="text-xs text-neutral-500">{bill.order_number} · {KS_LABEL[ks] || bill.status}</p>}
            </div>
            {bill && (
              <Button onClick={openUpi} className="bg-[var(--kul-primary)] text-white h-9" data-testid="waiter-upi">
                <QrCode className="w-4 h-4 mr-1" /> UPI {money(bill.total, sym)}
              </Button>
            )}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search menu…" className="pl-9 h-11" />
          </div>
          <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
            <button onClick={() => setActiveCat("all")} className={`px-3 py-1.5 rounded-full text-xs font-medium ${activeCat === "all" ? "bg-[var(--kul-primary)] text-white" : "bg-neutral-100"}`}>All</button>
            {cats.map((c) => (
              <button key={c.id} onClick={() => setActiveCat(c.id)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${activeCat === c.id ? "bg-[var(--kul-primary)] text-white" : "bg-neutral-100"}`}>{c.name}</button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 bg-[#FAFAFA]">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            {filtered.map((item) => (
              <button key={item.id} onClick={() => add(item)} className="kul-card overflow-hidden text-left" data-testid={`waiter-item-${item.id}`}>
                <div className="h-20 bg-neutral-100 relative">
                  {item.image && <img src={asset(item.image)} alt="" className="w-full h-full object-cover" />}
                  <span className="absolute top-1.5 left-1.5">
                    {item.is_veg
                      ? <span className="w-4 h-4 rounded border border-green-600 bg-white flex items-center justify-center"><Leaf className="w-2.5 h-2.5 text-green-600" /></span>
                      : <span className="w-4 h-4 rounded border border-red-600 bg-white flex items-center justify-center"><Beef className="w-2.5 h-2.5 text-red-600" /></span>}
                  </span>
                </div>
                <div className="p-2.5">
                  <div className="text-sm font-medium line-clamp-2">{item.name}</div>
                  <div className="mt-1 font-semibold text-[var(--kul-primary)]">{money(item.price, sym)}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="w-[360px] shrink-0 bg-white flex flex-col">
        <div className="p-4 border-b font-display font-700">Ticket</div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {bill?.items?.map((it, i) => (
            <div key={`b-${i}`} className="flex justify-between text-sm text-neutral-500">
              <span>{it.qty}× {it.name}</span><span>{money(it.price * it.qty, sym)}</span>
            </div>
          ))}
          {!!bill?.items?.length && cart.length > 0 && <div className="border-t pt-2 text-[11px] uppercase tracking-wide text-neutral-400">New items</div>}
          {cart.map((it) => (
            <div key={it.menu_item_id} className="flex items-center gap-2">
              <div className="flex-1 text-sm font-medium">{it.name}</div>
              <button onClick={() => setCart((c) => c.map((x) => x.menu_item_id === it.menu_item_id ? { ...x, qty: Math.max(1, x.qty - 1) } : x))}><Minus className="w-4 h-4" /></button>
              <span className="w-5 text-center text-sm">{it.qty}</span>
              <button onClick={() => setCart((c) => c.map((x) => x.menu_item_id === it.menu_item_id ? { ...x, qty: x.qty + 1 } : x))}><Plus className="w-4 h-4" /></button>
              <button onClick={() => setCart((c) => c.filter((x) => x.menu_item_id !== it.menu_item_id))}><Trash2 className="w-4 h-4 text-neutral-300" /></button>
            </div>
          ))}
          {!cart.length && !bill && <div className="text-neutral-400 text-sm py-10 text-center">Tap dishes to add</div>}
        </div>
        <div className="p-4 border-t space-y-2">
          {!!cart.length && <div className="flex justify-between font-semibold"><span>New</span><span>{money(cartTotal, sym)}</span></div>}
          {bill && <div className="flex justify-between font-display text-lg font-800"><span>Bill</span><span>{money(bill.total, sym)}</span></div>}
          <Button disabled={!cart.length || sending} onClick={sendKitchen} className="w-full h-11 bg-[var(--kul-primary)] text-white" data-testid="waiter-send">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ChefHat className="w-4 h-4 mr-1" /> {flow === "approve" && (!bill || bill.status === "awaiting_approval") ? "Send for approval" : "Send to kitchen"}</>}
          </Button>
        </div>
      </div>

      {payOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setPayOpen(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()} data-testid="waiter-upi-modal">
            <h3 className="font-display text-xl font-800">Pay with UPI</h3>
            <p className="text-sm text-neutral-500 mt-1">{upiInfo?.order_number} · {money(upiInfo?.amount, sym)}</p>
            {qrUrl && <img src={qrUrl} alt="UPI QR" className="mx-auto mt-4 w-64 h-64" />}
            <p className="text-xs text-neutral-400 mt-2">{upiInfo?.payee_name} · {upiInfo?.vpa}</p>
            <p className="text-xs text-neutral-500 mt-1">
              {upiInfo?.auto_confirm
                ? "Waiting for automatic UPI confirmation…"
                : `Customer scans this QR for exactly ${money(upiInfo?.amount, sym)}`}
            </p>
            <Button onClick={markPaid} className="w-full mt-4 h-11 bg-[var(--kul-primary)] text-white" data-testid="waiter-mark-paid">
              <CheckCircle2 className="w-4 h-4 mr-1" /> {upiInfo?.auto_confirm ? "Mark paid manually" : "Mark UPI paid"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
